import React, { useState, useMemo } from "react";
import { useCart } from "@/context/CartContext";
import { useAppContext } from "@/context/AppContext";
import { useNavigation } from "@/context/NavigationContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  Trash2,
  CreditCard,
  Clock,
  Plane,
  Activity,
  Package,
  MapPin,
  Compass
} from "lucide-react";
import PaymentModal from "@/components/PaymentModal";
import RemoveFromCartAlert from "@/components/RemoveFromCartAlert";
import InstrumentLoader from "@/components/InstrumentLoader";
import { message } from "antd";
import { PART_STATES, PART_CATEGORIES } from "../enums";
import { AppService } from "../services/appService";

const Cart = () => {
  const { cart, loading: cartLoading, removeCartItem, removeCartItemsLocally, clearCart } = useCart();
  const { loading: appLoading, processCardPayment } = useAppContext();
  const { navigateTo } = useNavigation();
  const { getDictValue } = useAuth();

  const [isPaymentOpen, setIsPaymentOpen] = useState(false);
  const [selectedItemsForPayment, setSelectedItemsForPayment] = useState<CartItemDto[] | null>(null);
  const [isFetchingItem, setIsFetchingItem] = useState(false);
  const [isDeleteAlertOpen, setIsDeleteAlertOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<any>(null);

  const formatPrice = (price: number) =>
    new Intl.NumberFormat("ro-RO", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(price);

  const parseDetails = (jsonString?: string) => {
    try {
      return jsonString ? JSON.parse(jsonString) : {};
    } catch (e) {
      return {};
    }
  };

  const handleItemDetailsNavigation = async (item: any) => {
    if (isFetchingItem || !item.productId) return;

    setIsFetchingItem(true);
    const isPart = item.type === "AIRCRAFT_PART";
    const isRental = item.type === "AIRCRAFT_RENTAL";
    const isExperience = item.type === "EXPERIENCE";

    try {
      if (isPart) {
        const partData = await AppService.getAircraftPart(item.productId);
        if (partData) {
          navigateTo("part-details", { part: partData, partId: item.productId });
        } else {
          message.error(getDictValue("cart.msg.part.error"));
        }
      } else if (isRental) {
        const rentalListingData = await AppService.getAircraftListing(item.productId);
        if (rentalListingData) {
          navigateTo("rental-details", { aircraftListing: rentalListingData, listingId: item.productId });
        } else {
          message.error(getDictValue("cart.msg.rental.error"));
        }
      } else if (isExperience) {
        const experienceData = await AppService.getExperience(item.productId);
        if (experienceData) {
          navigateTo("experience-details", { experience: experienceData, experienceId: item.productId });
        } else {
          message.error(getDictValue("cart.msg.experience.error"));
        }
      } else {
        const listingData = await AppService.getAircraftListing(item.productId);
        if (listingData) {
          navigateTo("aircraft-details", { aircraftListing: listingData, listingId: item.productId });
        } else {
          message.error(getDictValue("cart.msg.aircraft.error"));
        }
      }
    } catch (error) {
      console.error("Error fetching context item:", error);
      message.error(getDictValue("cart.msg.network.error"));
    } finally {
      setIsFetchingItem(false);
    }
  };

  const handleRemoveItem = async (cartItemDto: any) => {
    const hide = message.loading(getDictValue("cart.msg.removing"), 0);

    try {
      const success = await removeCartItem(cartItemDto);

      if (success) {
        message.success(getDictValue("cart.msg.remove.success"));
      } else {
        message.error(getDictValue("cart.msg.remove.fail"));
      }
    } catch (error) {
      console.error("Remove error:", error);
      message.error(getDictValue("cart.msg.server.error"));
    } finally {
      hide();
    }
  };

  const handleCheckoutAll = () => {
    if (activeTotal <= 0) return;

    const payableItems = items.filter(item => {
      const details = parseDetails(item.jsonDetails);
      const isPart = item.type === "AIRCRAFT_PART";
      const isRejected = details.Cancelled === "true" || details.Cancelled === "1" || details.Rejected === "true";
      const isPaymentEnabled = details.Payment === "ENABLED";

      return isPart || (!isRejected && isPaymentEnabled);
    });

    setSelectedItemsForPayment(payableItems);
    setIsPaymentOpen(true);
  };

  const items = cart?.cartItems || [];

  const activeTotal = useMemo(() => {
    return items.reduce((acc, item) => {
      const details = parseDetails(item.jsonDetails);

      if (item.type === "AIRCRAFT_PART") {
        return acc + (item.grossValue || 0);
      }

      const isRejected = details.Cancelled === "true" || details.Cancelled === "1" || details.Rejected === "true";
      const isPaymentEnabled = details.Payment === "ENABLED";

      if (!isRejected && isPaymentEnabled) {
        return acc + (item.grossValue || 0);
      }
      return acc;
    }, 0);
  }, [items]);

  const isSyncing = cartLoading || isFetchingItem;

  const openPaymentForItem = (item: any) => {
    setSelectedItemsForPayment([item]);
    setIsPaymentOpen(true);
  };

  return (
    <div className="min-h-screen bg-[#020617] text-slate-300 pb-20 font-sans">
      <InstrumentLoader visible={isSyncing} />

      <div className="container py-12 max-w-3xl px-6">
        <header className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12 border-l-4 border-blue-600 pl-6">
          <div>
            <p className="text-blue-400 font-mono text-sm tracking-[0.2em] mb-1">
              {getDictValue("cart.header")}
            </p>
            <h1 className="text-5xl font-black text-white tracking-tight italic uppercase">
              {getDictValue("cart.title")}
            </h1>
          </div>
          {!isSyncing && items.length > 0 && (
            <div className="flex items-center gap-4 bg-slate-900/50 p-2 rounded-2xl border border-slate-800">
              <div className="text-right px-4">
                <p className="text-[10px] text-slate-500 uppercase font-bold tracking-widest">
                  {getDictValue("cart.active.units")}
                </p>
                <p className="text-blue-500 font-mono text-xl font-black">{items.length}</p>
              </div>
              <Package className="text-slate-700 mr-2" size={20} />
            </div>
          )}
        </header>

        {!isSyncing && (
          <div className="animate-in fade-in slide-in-from-bottom-4 duration-700">
            {items.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-24 bg-slate-900/10 rounded-[3rem] border border-dashed border-slate-800">
                <Plane size={48} className="text-slate-700 -rotate-45 mb-6 opacity-20" />
                <h4 className="text-white font-black uppercase tracking-widest mb-2">
                  {getDictValue("cart.empty.txb")}
                </h4>
                <Button onClick={() => navigateTo("aircraft")} className="mt-4 bg-blue-600 hover:bg-blue-500 text-white font-black rounded-xl uppercase">
                  {getDictValue("cart.explore.button")}
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {items.map((item, index) => {
                  const details = parseDetails(item.jsonDetails);
                  const isPart = item.type === "AIRCRAFT_PART";
                  const isExperience = item.type === "EXPERIENCE";
                  const isRejected = !isPart && (details.Cancelled === "true" || details.Cancelled === "1" || details.Rejected === "true");

                  const isPaymentDisabled = !isPart && details.Payment === "DISABLED";
                  const isCheckoutActive = !isPart && details.Checkout === "true";

                  const slotKeys = Object.keys(details).filter(key => key.startsWith("Slot") && key !== "Slot");
                  const now = Date.now();
                  const seventyTwoHoursInMs = 72 * 60 * 60 * 1000;

                  const getEarliestSlotTimestamp = () => {
                    const activeSlotKey = isExperience ? "Slot" : slotKeys[0];
                    if (!details.Date || (!details[activeSlotKey] && slotKeys.length === 0)) return null;

                    try {
                      const targetSlotValue = isExperience ? details["Slot"] : details[activeSlotKey];
                      const startTime = targetSlotValue.split("-")[0].trim();
                      const fullDateString = `${details.Date}T${startTime}:00`;
                      const t = new Date(fullDateString).getTime();
                      return isNaN(t) ? null : t;
                    } catch (e) {
                      return null;
                    }
                  };

                  const earliestSlot = getEarliestSlotTimestamp();
                  const isWithin72Hours = earliestSlot && (earliestSlot - now < seventyTwoHoursInMs) && (earliestSlot - now > 0);
                  const isDeleteDisabled = !isPart && !isRejected && ((isPaymentDisabled && isCheckoutActive) || isWithin72Hours);

                  const isPaymentClickable = isPart || (details.Payment === "ENABLED" && !isRejected);
                  const slots = slotKeys.map(key => details[key]);

                  const firstImage = item.mediaFiles && item.mediaFiles.length > 0
                    ? item.mediaFiles[0]
                    : null;

                  const categoryLabel = isPart ? PART_CATEGORIES.find(c => c.code === details.Category)?.label || details.Category : null;
                  const stateLabel = isPart ? PART_STATES.find(s => s.code === details.State)?.label || details.State : null;

                  return (
                    <div
                      key={item.productId || index}
                      className={`bg-[#0F172A]/80 backdrop-blur-xl border rounded-3xl p-4 flex flex-col gap-4 relative overflow-hidden group transition-all duration-300 ${
                        isRejected ? "border-red-900/50 bg-red-950/10 shadow-[0_0_15px_rgba(220,38,38,0.1)]" : "border-slate-800"
                      }`}
                    >
                      {isRejected && (
                        <div className="absolute top-0 right-0 bg-red-600 text-white text-[8px] font-black px-3 py-1 uppercase tracking-tighter rounded-bl-xl z-10 animate-pulse">
                          {getDictValue("cart.badge.rejected")}
                        </div>
                      )}

                      <div className={`flex items-center gap-4 ${isPart ? "py-2 min-h-[80px]" : ""}`}>
                        <div className="h-20 w-20 shrink-0 rounded-xl bg-slate-950 border border-slate-800 flex items-center justify-center relative overflow-hidden">
                          {firstImage ? (
                            <img
                              src={firstImage}
                              className={`w-full h-full object-cover transition-transform duration-700 ${isRejected ? "grayscale opacity-50" : "group-hover:scale-110"}`}
                              alt="Unit Preview"
                            />
                          ) : (
                            <div className="flex flex-col items-center opacity-40">
                              {isPart ? (
                                <Package className="h-6 w-6 text-emerald-500" />
                              ) : isExperience ? (
                                <Compass className="h-6 w-6 text-red-500" />
                              ) : (
                                <Plane className={`h-6 w-6 ${item.type === "AIRCRAFT_RENTAL" ? "text-blue-500" : "text-orange-500"}`} />
                              )}
                              <Activity className="h-4 w-4 mt-1 text-slate-500" />
                            </div>
                          )}
                        </div>

                        <div className="flex-1 min-w-0">
                          <div
                            onClick={() => handleItemDetailsNavigation(item)}
                            className={`text-sm font-black uppercase tracking-tight truncate ${isRejected ? "text-red-400" : "text-white hover:text-blue-400 cursor-pointer transition-colors"}`}
                          >
                            {isPart
                              ? `${details.Manufacturer || "OEM"} // ${details.Title || ""}`
                              : isExperience
                                ? details.Title
                                : (details.Pachet || details.Aircraft || item.type)}
                          </div>

                          <div className="text-[10px] font-bold space-y-1 mt-1">
                            <p className={isPart ? "text-emerald-400" : isExperience ? "text-red-400" : isRejected ? "text-red-500/70" : (item.type === "AIRCRAFT_RENTAL" ? "text-blue-400" : "text-orange-400")}>
                              {isPart
                                ? getDictValue("cart.type.part")
                                : isExperience
                                  ? getDictValue("cart.type.experience")
                                  : isRejected
                                    ? getDictValue("cart.type.rejected")
                                    : (item.type === "AIRCRAFT_RENTAL"
                                      ? getDictValue("cart.type.rental")
                                      : getDictValue("cart.type.sale"))}
                            </p>
                            <div className="text-slate-500 flex flex-wrap gap-x-3">
                              {isPart ? (
                                <>
                                  {categoryLabel && <span>{getDictValue("cart.item.cat")}: {categoryLabel}</span>}
                                  {details.SerialNumber && <span className="text-emerald-500/60 font-mono text-[9px]">S/N: {details.SerialNumber}</span>}
                                  {stateLabel && <span className="bg-emerald-500/10 text-emerald-500 px-1.5 rounded uppercase">{getDictValue("cart.item.state")}: {stateLabel}</span>}
                                </>
                              ) : isExperience ? (
                                <>
                                  {details.Date && <span>{getDictValue("cart.item.date")}: {details.Date}</span>}
                                  {details.Type && <span className="text-red-400/80">{getDictValue("cart.item.type")}: {details.Type}</span>}
                                  {details.Duration && <span>{getDictValue("cart.item.duration")}: {details.Duration} MIN</span>}
                                  {details.Location && (
                                    <span className="flex items-center gap-1 text-red-400/60">
                                      <MapPin size={10} /> {details.Location}
                                    </span>
                                  )}
                                </>
                              ) : (
                                <>
                                  {details.Date && <span>{getDictValue("cart.item.date")}: {details.Date}</span>}
                                  {details.Identification && <span>{getDictValue("cart.item.tail")}: {details.Identification}</span>}
                                  {details.Location && (
                                    <span className="flex items-center gap-1 text-blue-400/60">
                                      <MapPin size={10} /> {details.Location}
                                    </span>
                                  )}
                                </>
                              )}
                            </div>

                            {!isPart && (
                              <div className="flex flex-wrap gap-1 mt-1">
                                {isExperience && details.Slot ? (
                                  <span className="bg-slate-900 px-2 py-0.5 rounded text-[9px] font-mono text-red-300 border border-slate-800">
                                    ({details.Slot})
                                  </span>
                                ) : (
                                  slots.map((slot, idx) => (
                                    <span key={idx} className="bg-slate-900 px-2 py-0.5 rounded text-[9px] font-mono text-blue-300 border border-slate-800">
                                      ({slot})
                                    </span>
                                  ))
                                )}
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="text-right shrink-0">
                          <span className={`text-lg font-black block leading-none ${isRejected ? "text-slate-600 line-through" : "text-white"}`}>
                            {formatPrice(item.grossValue || 0)}
                          </span>
                        </div>

                        <div className="flex items-center gap-2">
                          {isPart && (
                            <Button
                              onClick={() => openPaymentForItem(item)}
                              className="h-9 px-4 rounded-xl font-black uppercase text-[10px] tracking-widest transition-all bg-emerald-600 hover:bg-emerald-700 text-white cursor-pointer shadow-lg shadow-emerald-900/20"
                            >
                              <CreditCard size={12} className="mr-2" />
                              {getDictValue("cart.button.buy")}
                            </Button>
                          )}
                          <button
                            onClick={() => {
                              setItemToDelete(item);
                              setIsDeleteAlertOpen(true);
                            }}
                            disabled={isDeleteDisabled}
                            className={`p-2 transition-all duration-200 rounded-lg ${
                              isDeleteDisabled
                                ? "text-slate-600/50 cursor-not-allowed bg-slate-900/30 border border-slate-800/50"
                                : "text-slate-400 hover:text-red-500 hover:bg-red-500/10 active:scale-95"
                            }`}
                            title={isDeleteDisabled ? getDictValue("cart.action.restricted") : getDictValue("cart.action.delete")}
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>

                      {!isPart && (
                        <div className="pt-3 border-t border-slate-800/50 flex justify-between items-center">
                          <div className="flex-1">
                            {isRejected ? (
                              <p className="text-[10px] text-red-400/80 font-medium italic leading-tight max-w-[250px]">
                                {getDictValue("cart.info.rejected")}
                              </p>
                            ) : isDeleteDisabled ? (
                              <p className="text-[10px] text-amber-500/80 font-medium italic leading-tight max-w-[250px]">
                                {isWithin72Hours
                                  ? getDictValue("cart.info.cancel.72h")
                                  : getDictValue("cart.info.processing")}
                              </p>
                            ) : isPaymentClickable ? (
                              <p className="text-[10px] text-emerald-400/80 font-medium italic leading-tight max-w-[250px]">
                                {getDictValue("cart.info.confirmed")}
                              </p>
                            ) : (
                              <p className="text-[10px] text-slate-500 font-medium italic leading-tight max-w-[250px]">
                                {getDictValue("cart.info.awaiting")}
                              </p>
                            )}
                          </div>

                          <div className="flex items-center gap-3">
                            {isRejected ? (
                              <Button
                                onClick={() => navigateTo("aircraft")}
                                className="h-8 px-4 rounded-lg font-black uppercase text-[10px] bg-red-600/20 hover:bg-red-600/40 text-red-400 border border-red-900/50"
                              >
                                {getDictValue("cart.button.new.booking")}
                              </Button>
                            ) : (
                              <>
                                {!isPaymentClickable && (
                                  <span className="text-[9px] text-amber-500 font-black animate-pulse uppercase tracking-tighter">
                                    {getDictValue("cart.status.clearance")}
                                  </span>
                                )}
                                <div className={!isPaymentClickable ? "cursor-not-allowed" : ""}>
                                  <Button
                                    onClick={() => openPaymentForItem(item)}
                                    disabled={!isPaymentClickable}
                                    className={`h-8 px-4 rounded-lg font-black uppercase text-[10px] tracking-widest transition-all ${
                                      isPaymentClickable
                                        ? "bg-emerald-600 hover:bg-emerald-700 text-white cursor-pointer"
                                        : "bg-slate-800 text-slate-600 opacity-50 pointer-events-none"
                                    }`}
                                  >
                                    <CreditCard size={12} className="mr-2" />
                                    {isPaymentClickable ? getDictValue("cart.button.pay") : getDictValue("cart.button.blocked")}
                                  </Button>
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}

                <div className="bg-blue-600/5 border border-blue-500/20 rounded-3xl p-6 mt-8 flex flex-col sm:flex-row items-center justify-between gap-6">
                  <div>
                    <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest">
                      {getDictValue("cart.total.label")}
                    </p>
                    <p className="text-4xl font-black text-white tracking-tighter font-mono">
                      {formatPrice(activeTotal)}
                    </p>
                  </div>

                  <div className="flex gap-3 w-full sm:w-auto">
                    <Button
                      disabled={activeTotal === 0}
                      onClick={handleCheckoutAll}
                      className={`flex-1 sm:flex-none bg-blue-600 hover:bg-blue-500 text-white font-black px-10 h-12 rounded-2xl uppercase tracking-widest text-xs shadow-lg shadow-blue-900/20 transition-all ${
                        activeTotal === 0 ? "opacity-50 cursor-not-allowed grayscale" : "active:scale-95"
                      }`}
                    >
                      {getDictValue("cart.button.checkout")}
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {selectedItemsForPayment && (
          <PaymentModal
            isOpen={isPaymentOpen}
            onClose={() => {
              setIsPaymentOpen(false);
              setSelectedItemsForPayment(null);
            }}
            itemsToPay={selectedItemsForPayment}
            processCardPayment={processCardPayment}
            onSuccess={() => {
              const isBulk = selectedItemsForPayment.length > 1;
              removeCartItemsLocally(selectedItemsForPayment);

              message.success(isBulk
                ? getDictValue("cart.msg.bulk.pay.success")
                : `${getDictValue("cart.msg.pay.success")} ${parseDetails(selectedItemsForPayment[0].jsonDetails).Title || parseDetails(selectedItemsForPayment[0].jsonDetails).Aircraft || ''}`
              );
            }}
          />
        )}
        <RemoveFromCartAlert
          isOpen={isDeleteAlertOpen}
          itemType={itemToDelete?.type}
          itemTitle={
            itemToDelete
              ? (parseDetails(itemToDelete.jsonDetails).Title || parseDetails(itemToDelete.jsonDetails).Pachet || parseDetails(itemToDelete.jsonDetails).Aircraft || "")
              : ""
          }
          onClose={() => {
            setIsDeleteAlertOpen(false);
            setItemToDelete(null);
          }}
          onConfirm={async () => {
            if (itemToDelete) {
              await handleRemoveItem(itemToDelete);
            }
            setIsDeleteAlertOpen(false);
            setItemToDelete(null);
          }}
        />
      </div>
    </div>
  );
};

export default Cart;